import {
  collection,
  doc,
  getDocs,
  getDoc,
  updateDoc,
  query,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import type { AccountUser, AppRoles, AccessPolicy, UserType, AuthType } from '../types';

const USERS_COL = collection(db, 'users');

// Audit stamp — every users/{uid} write should carry { updatedAt, updatedBy }
// so the onUserWrite trigger can attribute the change to a specific admin.
function actorStamp() {
  return { updatedAt: serverTimestamp(), updatedBy: auth.currentUser?.uid ?? null };
}

// ─── Fetch ─────────────────────────────────────────────────────────────────────

export async function fetchAllUsers(): Promise<AccountUser[]> {
  const snap = await getDocs(query(USERS_COL, orderBy('name')));
  return snap.docs.map(d => ({
    id: d.id,
    uid: d.id,
    ...d.data(),
  } as AccountUser));
}

export async function fetchUser(uid: string): Promise<AccountUser | null> {
  const snap = await getDoc(doc(db, 'users', uid));
  if (!snap.exists()) return null;
  return { id: snap.id, uid: snap.id, ...snap.data() } as AccountUser;
}

// ─── Backward-compat shim (Phase 5) ──────────────────────────────────────────
//
// Effective profile = user fields, with HRIS fields preferred from the linked
// contact (if user.contactId is set). This lets UI gradually shift to contact
// fields without breaking pages that read AccountUser directly.
//
// Removal scheduled in Phase 14 once all consumer pages read directly from
// contacts/ via the SDK.

export async function getEffectiveProfile(uid: string): Promise<AccountUser | null> {
  const user = await fetchUser(uid);
  if (!user) return null;

  const contactId = (user as any).contactId as string | undefined;
  if (!contactId) return user;

  try {
    const contactSnap = await getDoc(doc(db, 'contacts', contactId));
    if (!contactSnap.exists()) return user;
    const contact = contactSnap.data() as any;

    // Merge: contact field wins for HRIS-ish fields, user fields keep all else.
    // Fields preferred from contact:
    const HRIS_FIELDS = [
      'displayName',
      'phone',
      'jobTitle',
      'grade',
      'department',
      'employee_id',
      'employment_type',
      'site',
      'startDate',
      'bio',
      'nationality',
      'emergencyContact',
      'photoURL',
      'unitId',
      'unitName',
      'directManagerId',
      'isLeader',
      'user_type',
    ] as const;

    const merged: any = { ...user };
    for (const f of HRIS_FIELDS) {
      if (contact[f] !== undefined && contact[f] !== null && contact[f] !== '') {
        // Map displayName ↔ name for AccountUser compatibility
        if (f === 'displayName') merged.name = contact[f];
        else merged[f] = contact[f];
      }
    }
    return merged as AccountUser;
  } catch {
    // If contact read fails, fall back to user fields only
    return user;
  }
}

// ─── Update profile fields ─────────────────────────────────────────────────────

export async function updateUserProfile(
  uid: string,
  updates: Partial<Pick<AccountUser, 'name' | 'jobTitle' | 'grade' | 'unitId' | 'isActive'>>,
): Promise<void> {
  // Strip undefined values — Firestore rejects `undefined` (must be null
  // or omitted). Empty strings stay as-is; caller decides when to clear
  // a field by passing null explicitly.
  const clean = Object.fromEntries(Object.entries(updates).filter(([, v]) => v !== undefined));
  if (Object.keys(clean).length === 0) return;
  await updateDoc(doc(db, 'users', uid), { ...clean, ...actorStamp() });
}

// ─── Per-app role mapping ──────────────────────────────────────────────────────

export async function updateAppRoles(uid: string, appRoles: AppRoles): Promise<void> {
  await updateDoc(doc(db, 'users', uid), { appRoles, ...actorStamp() });
}

/** Update a single app's role without touching the others. */
export async function updateSingleAppRole(
  uid: string,
  app: keyof AppRoles,
  role: string,
): Promise<void> {
  await updateDoc(doc(db, 'users', uid), {
    [`appRoles.${app}`]: role,
    ...actorStamp(),
  });
}

/** Bulk-update a single app role for multiple users at once. */
export async function bulkUpdateAppRole(
  uids: string[],
  app: keyof AppRoles,
  role: string,
): Promise<void> {
  await Promise.all(
    uids.map(uid =>
      updateDoc(doc(db, 'users', uid), {
        [`appRoles.${app}`]: role,
        ...actorStamp(),
      }),
    ),
  );
}

// ─── Manager mapping ───────────────────────────────────────────────────────────

export async function updateDirectManager(uid: string, managerId: string | null): Promise<void> {
  await updateDoc(doc(db, 'users', uid), {
    directManagerId: managerId ?? null,
    ...actorStamp(),
  });
}

/** Batch update manager assignments. */
export async function bulkUpdateManagers(
  assignments: { uid: string; managerId: string | null }[],
): Promise<void> {
  await Promise.all(
    assignments.map(({ uid, managerId }) =>
      updateDoc(doc(db, 'users', uid), {
        directManagerId: managerId ?? null,
        ...actorStamp(),
      }),
    ),
  );
}

// ─── Extended HRIS profile ─────────────────────────────────────────────────────

export async function updateExtendedProfile(
  uid: string,
  updates: Partial<Pick<
    import('../types').AccountUser,
    'phone' | 'department' | 'startDate' | 'bio' | 'nationality' | 'emergencyContact' | 'employee_id' | 'site' | 'employment_type'
  >>,
): Promise<void> {
  // Strip undefined values
  const clean = Object.fromEntries(Object.entries(updates).filter(([, v]) => v !== undefined));
  if (Object.keys(clean).length === 0) return;
  await updateDoc(doc(db, 'users', uid), { ...clean, ...actorStamp() });
}

// ─── Access Policy (global override) ──────────────────────────────────────────

export async function updateAccessPolicy(uid: string, access_policy: AccessPolicy): Promise<void> {
  await updateDoc(doc(db, 'users', uid), { access_policy, ...actorStamp() });
}

// ─── User type & auth type ────────────────────────────────────────────────────

export async function updateUserType(uid: string, user_type: UserType): Promise<void> {
  await updateDoc(doc(db, 'users', uid), { user_type, ...actorStamp() });
}

export async function updateAuthType(uid: string, auth_type: AuthType): Promise<void> {
  await updateDoc(doc(db, 'users', uid), { auth_type, ...actorStamp() });
}

// ─── Active / Inactive toggle ──────────────────────────────────────────────────

export async function setUserActive(uid: string, isActive: boolean): Promise<void> {
  await updateDoc(doc(db, 'users', uid), { isActive, ...actorStamp() });
}

// ─── Per-app login disable toggle ─────────────────────────────────────────────
// See docs/ACCESS_MODEL.md §"Per-app access control". Setting any app's flag
// to `true` forces signOut in that app's AuthContext on next page load.

export type AppKey = 'okr' | 'epr' | 'portal';

export async function setAppDisabled(uid: string, app: AppKey, disabled: boolean): Promise<void> {
  await updateDoc(doc(db, 'users', uid), {
    [`appDisabled.${app}`]: disabled,
    ...actorStamp(),
  });
}

// ─── Org fields (unit / manager / leader / employeeLevel) ─────────────────────
// Editable post-invite from UserSlideOver. Keep the schema-canonical shape
// consistent with what inviteUser Cloud Function writes.

export interface OrgFieldUpdates {
  unitId?:           string | null;
  unitName?:         string | null;
  directManagerId?:  string | null;
  isLeader?:         boolean;
  employeeLevel?:    'general' | 'supervisor' | 'dept_head' | null;
}

export async function updateOrgFields(uid: string, updates: OrgFieldUpdates): Promise<void> {
  const clean = Object.fromEntries(Object.entries(updates).filter(([, v]) => v !== undefined));
  if (Object.keys(clean).length === 0) return;
  await updateDoc(doc(db, 'users', uid), { ...clean, ...actorStamp() });
}

// ─── App roles editable from UserSlideOver ────────────────────────────────────
// Updates appRoles + mirrors appRoles.okr to flat `role` for OKR Tracker
// backward-compat (same logic as inviteUser Cloud Function).

export async function setAppRoles(
  uid: string,
  appRoles: { okr?: string; epr?: string; portal?: string },
): Promise<void> {
  const flatRole = appRoles.okr || 'viewer';
  await updateDoc(doc(db, 'users', uid), {
    appRoles,
    role: flatRole,
    ...actorStamp(),
  });
}

// ─── Fetch pending users (status = 'pending') ─────────────────────────────────

export async function fetchPendingUsers(): Promise<import('../types').AccountUser[]> {
  const { where } = await import('firebase/firestore');
  const snap = await getDocs(query(USERS_COL, where('status', '==', 'pending'), orderBy('invitedAt', 'desc')));
  return snap.docs.map(d => ({ id: d.id, uid: d.id, ...d.data() } as import('../types').AccountUser));
}

// ─── Delete user from Firestore (for cancelled invitations) ───────────────────

export async function deleteUserDoc(uid: string): Promise<void> {
  const { deleteDoc } = await import('firebase/firestore');
  await deleteDoc(doc(db, 'users', uid));
}
